import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { UserSettingsService } from './user-settings.service';
import { AttributePresetStore, PINNED_OFF_MIRROR } from './attribute-preset-store.base';
import {
  coerceContrast,
  resolveContrast,
  ContrastPreset,
  ResolvedContrast,
  CONTRAST_KEY,
  CONTRAST_STORAGE_KEY,
} from './contrast.util';

/**
 * App-wide cache of the user's contrast preference, and the only writer of the `data-contrast`
 * attribute on <html>.
 *
 * A sibling of ThemeService in every respect that matters: same base, same boot-mirror design,
 * same live 'system' preset — only the vocabulary differs. The parts that make a preset visible
 * (the boot-stamp deference, the promote-an-equal-default adopt, the persist-before-emit ordering
 * in setPreset, the zone-re-entered media query) live in {@link AttributePresetStore} and are not
 * re-derived here; re-deriving them by hand is how each of them originally shipped broken.
 *
 * Two things ARE specific to contrast:
 *  - the resolved value is nullable, because normal contrast is the attribute's ABSENCE (the
 *    token blocks match on `html[data-contrast='more']`); and
 *  - the mirror therefore has three states rather than two, so a user who pinned normal contrast
 *    on an OS that prefers more does not get a high-contrast flash on every cold load. See
 *    PINNED_OFF_MIRROR.
 *
 * `preset$` / `currentPreset` / `resolved$` / `setPreset` are all inherited: `resolved$` is what
 * the Preferences preview binds to, since 'system' says nothing on its own about what is painted.
 */
@Injectable({ providedIn: 'root' })
export class ContrastService extends AttributePresetStore<ContrastPreset, ResolvedContrast> {
  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    @Inject(DOCUMENT) doc: Document,
  ) {
    super(userSettings, store, zone, doc, {
      settingsKey: CONTRAST_KEY,
      attributeName: 'data-contrast',
      mirrorKey: CONTRAST_STORAGE_KEY,
      mirrorArea: 'local',
      mediaQuery: '(prefers-contrast: more)',
    }, 'system');
  }

  protected coerce(v: unknown): ContrastPreset {
    return coerceContrast(v);
  }

  protected resolve(preset: ContrastPreset, prefersMore: boolean): ResolvedContrast {
    return resolveContrast(preset, prefersMore);
  }

  /**
   * The contrast boot script's whitelist, restated: 'more' is stamped, the PINNED_OFF_MIRROR
   * sentinel means "the user pinned normal, do not ask the OS" — a real answer of `null`, not a
   * miss — and anything else is ignored and left to prefers-contrast. index.boot-theme.spec.ts
   * pins the script's half of this; the two must not drift.
   */
  protected resolvedFromMirror(mirror: string): ResolvedContrast | undefined {
    if (mirror === 'more') return 'more';
    if (mirror === PINNED_OFF_MIRROR) return null;
    return undefined;
  }
}
